import { Fragment, FunctionComponent, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";

import "./ImageGenerationPage.scss";

import { useTranslation } from "react-i18next";

import { Suggestions } from "../../components/Shared";
import { TextMarkdown } from "../../components/Shared/Components";
import { PendingDotsSkeleton } from "../../components/Shared/LoadingSkeletons/PendingDotsSkeleton/PendingDotsSkeleton";
import { translationKeys } from "../../models/app-translation-keys";
import { useStore } from "../../stores/StoreContext";
import { ImageAiModelControl } from "./ImageAiModelControl/ImageAiModelControl";
import { ImageGenerationPrompt } from "./ImageGenerationPrompt/ImageGenerationPrompt";
import { ImageGrid } from "./ImageGrid/ImageGrid";

export const ImageGenerationPage: FunctionComponent = observer(() => {
  const { t } = useTranslation();
  const [showImageArea, setShowImageArea] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>("");
  const [cancellingGeneration, setCancellingGeneration] = useState<boolean>(false);

  const { imageGenerationStore, globalErrorsStore } = useStore();

  const featureTranslationKeys = translationKeys.feature.imageGeneration;

  useEffect(() => {
    const hasImageGenErrors = globalErrorsStore.hasSpecificErrors([
      featureTranslationKeys.errors.requestFailed,
      featureTranslationKeys.errors.invalidAiModel,
      featureTranslationKeys.errors.invalidImageCount,
      featureTranslationKeys.errors.invalidColor,
      featureTranslationKeys.errors.invalidResolution,
      featureTranslationKeys.errors.invalidQuality,
    ]);

    const showContent =
      !hasImageGenErrors &&
      (!!imageGenerationStore?.activeProcess?.processId ||
        !!imageGenerationStore?.thumbnailsMap ||
        imageGenerationStore.generatedImageIds.length > 0);

    setShowImageArea(showContent);
  }, [imageGenerationStore.generatedImageIds, imageGenerationStore?.activeProcess]);

  const selectSuggestion = (suggestion: string) => {
    setSelectedSuggestion(suggestion);
  };

  return (
    <Fragment>
      <div className="image-generation-component">
        <div className="image-generation-action-bar">
          <ImageAiModelControl
            disabled={imageGenerationStore.isGeneratingImages}
            imageAiModel={imageGenerationStore.selectedAiModel}
            onChange={imageGenerationStore.setSelectedAiModel}
          />
        </div>

        <div className="image-generation-content">
          <div className="images" style={{ display: showImageArea ? "block" : "none" }}>
            <ImageGrid />
          </div>

          <div className="prompt-container">
            {!showImageArea && (
              <Fragment>
                <Suggestions
                  suggestionCount={3}
                  suggestionsTranslationArray={featureTranslationKeys.suggestions}
                  selectSuggestion={selectSuggestion}
                  id="image-generation-suggestion"
                />

                <TextMarkdown className="new-thread-question">
                  {t(translationKeys.feature.imageGeneration.greeting)}
                </TextMarkdown>
              </Fragment>
            )}

            {(imageGenerationStore.loadingThumbnails ||
              imageGenerationStore.loadingImageIds ||
              cancellingGeneration) && (
              <PendingDotsSkeleton
                translationKey={
                  cancellingGeneration
                    ? featureTranslationKeys.actions.cancellingImageGeneration
                    : !(imageGenerationStore.loadingThumbnails || imageGenerationStore.loadingImageIds)
                      ? featureTranslationKeys.actions.loadingData
                      : featureTranslationKeys.actions.loadingAIImageGeneration
                }
              />
            )}

            <ImageGenerationPrompt
              selectedSuggestion={selectedSuggestion}
              maxCharCount={32_000}
              cancellingGeneration={cancellingGeneration}
              setCancellingGeneration={setCancellingGeneration}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
});
